<template>
  <div class="empty-state" :class="{ 'empty-state--compact': compact }">
    <i v-if="icon" :class="icon" class="empty-state-icon" aria-hidden="true"></i>
    <component :is="tag" v-if="title" class="empty-state-title">{{ title }}</component>
    <p v-if="resolvedMessage || $slots.message" class="empty-state-message">
      <slot name="message">{{ resolvedMessage }}</slot>
    </p>
    <div v-if="$slots.actions || actionLabel" class="empty-state-actions">
      <slot name="actions">
        <Button 
          v-if="actionLabel" 
          :variant="actionVariant" 
          :icon="actionIcon"
          @click="$emit('action')"
        >
          {{ actionLabel }}
        </Button>
      </slot>
    </div>
    <slot></slot>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Button from './Button.vue'

const props = defineProps({
  icon: {
    type: String,
    default: null
  },
  title: {
    type: String,
    default: null
  },
  /**
   * Balise du titre. `h3` sous le titre d'une page ; `h1` quand l'état vide EST
   * la page (la page introuvable) — un seul `h1` par page.
   */
  tag: {
    type: String,
    default: 'h3',
    validator: (value) => ['h1', 'h2', 'h3'].includes(value)
  },
  message: {
    type: String,
    default: null
  },
  /**
   * Alias de `message`. Quatre écrans le passaient déjà sans qu'il existe : leur
   * phrase d'explication ne s'affichait nulle part. Le déclarer coûte moins que
   * de réécrire quatre appels, et referme le piège pour les suivants.
   */
  description: {
    type: String,
    default: null
  },
  /**
   * Version resserrée pour un état vide DANS une section (liste d'adresses,
   * d'argumentaires…) : le gabarit pleine page écrase une carte de fiche.
   */
  compact: {
    type: Boolean,
    default: false
  },
  actionLabel: {
    type: String,
    default: null
  },
  actionVariant: {
    type: String,
    default: 'primary'
  },
  actionIcon: {
    type: String,
    default: null
  }
})

defineEmits(['action'])

const resolvedMessage = computed(() => props.message || props.description)
</script>

<style scoped>
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-12);
  text-align: center;
  color: var(--color-text-tertiary);
}

.empty-state-icon {
  font-size: 3rem;
  color: var(--color-text-muted);
  margin-bottom: var(--spacing-4);
  opacity: 0.4;
}

.empty-state-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-2);
}

.empty-state-message {
  font-size: var(--font-size-base);
  color: var(--color-text-tertiary);
  margin-bottom: var(--spacing-4);
  line-height: var(--line-height-relaxed);
  max-width: 400px;
}

.empty-state-actions {
  margin-top: var(--spacing-md);
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
  justify-content: center;
}

/* Même composition, un cran plus bas dans l'échelle : l'état vide d'une section
   ne doit pas peser plus lourd que la section pleine. */
.empty-state--compact {
  padding: var(--spacing-6);
}

.empty-state--compact .empty-state-icon {
  font-size: 2rem;
  margin-bottom: var(--spacing-3);
}

.empty-state--compact .empty-state-title {
  font-size: var(--font-size-lg);
}

.empty-state--compact .empty-state-message {
  font-size: var(--font-size-sm);
}

.empty-state--compact .empty-state-actions {
  margin-top: 0;
}
</style>

